import MainTable from '@Components/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	EnrollmentTypeCard,
	TypeInfo,
	TypeName,
	TypeValue,
} from '@Components/UI/Analytics/Analytics.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import { Box, TableCell, TableRow } from '@mui/material';
import { blue, deepPurple, green, orange, red, yellow } from '@mui/material/colors';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { TableHeader } from '@Features/generics/generics.type';
import React, { useCallback, useEffect, useState } from 'react';
import { egp, money } from './EnrollmentInvoices';

type ProductScope = 'standalone-course' | 'bundle';

/** The hub's six buckets — promocode is derived out of wallet server-side. */
const METHOD_KEYS = ['wallet', 'promocode', 'scratchcard', 'admin', 'instructor', 'free'] as const;
type MethodKey = (typeof METHOD_KEYS)[number];
const METHOD_LABELS: Record<MethodKey, string> = {
	wallet: 'Wallet',
	promocode: 'Promocode',
	scratchcard: 'Scratchcard',
	admin: 'Admin',
	instructor: 'Instructor',
	free: 'Free',
};

type MethodBucket = { count: number; total: number };
type ReportRow = { list_price: number; effective_price: number; note: string } & Record<
	MethodKey,
	MethodBucket
>;
type ReportSummary = Record<MethodKey, MethodBucket> & {
	grand_total: number;
	refunded: number;
	refund_count: number;
	net: number;
};

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'List Price - Inv. Price', fieldName: 'prices', canSort: false, width: '14%' },
	{ displayName: 'Note', fieldName: 'note', canSort: false, width: '12%' },
	...METHOD_KEYS.map((method) => ({
		displayName: METHOD_LABELS[method],
		fieldName: method,
		canSort: false,
	})),
	{ displayName: 'Enrollments', fieldName: 'enrollments', canSort: false },
	{ displayName: 'Row Total', fieldName: 'row_total', canSort: false },
];

const pickMethodColor = (method: string) => {
	if (method === 'admin') return red[500];
	if (method === 'wallet') return deepPurple[500];
	if (method === 'promocode') return blue[500];
	if (method === 'scratchcard') return orange[500];
	if (method === 'free') return yellow[600];
	return green.A700;
};

const formula = (row: ReportRow, method: MethodKey) => {
	const count = Number(row[method]?.count ?? 0);
	const total = Number(row[method]?.total ?? 0);
	if (!count) return '0';
	if (method === 'free') return `${count} free`;
	return `${count} x ${money(row.effective_price)} = ${money(total)}`;
};

const rowEnrollments = (row: ReportRow) =>
	METHOD_KEYS.reduce((total, method) => total + Number(row[method]?.count ?? 0), 0);

const rowTotal = (row: ReportRow) =>
	METHOD_KEYS.reduce((total, method) => total + Number(row[method]?.total ?? 0), 0);

/**
 * The classroom Invoices tab's price-group report for a single standalone
 * course or bundle: latest snapshot per live enrollment, grouped by
 * (list price, paid price, note), split into the six method buckets. The
 * price groups themselves are the rows — the entity is single, so the
 * classroom design's expandable detail level is the top level here.
 */
const ProductEnrollmentInvoices: React.FC<{ scope: ProductScope; productId: number }> = ({
	scope,
	productId,
}) => {
	const { displaySnackbar } = useSnackbar();
	const [rows, setRows] = useState<ReportRow[]>([]);
	const [summary, setSummary] = useState<ReportSummary | null>(null);
	const [meta, setMeta] = useState<any>(null);

	const fetchReport = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			try {
				const endpoint =
					scope === 'bundle' ? 'bundles/invoices/report' : 'standalone-courses/invoices/report';
				const idKey = scope === 'bundle' ? 'bundle_id' : 'standalone_course_id';
				const response = await api.post(endpoint, {
					[idKey]: productId,
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					from: request.startDate,
					to: request.endDate,
				});
				const data = response.data.data || {};
				setRows(data.rows || []);
				setSummary(data.summary || null);
				setMeta(response.data.meta || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load the invoices report'
				);
			}
		},
		[displaySnackbar, productId, scope]
	);

	useEffect(() => {
		// Parity with the classroom Invoices tab: default to the last 14 days.
		const startDate = new Date();
		startDate.setDate(startDate.getDate() - 14);
		fetchReport({ activePage: 1, perPage: 10, startDate, endDate: new Date() });
	}, [fetchReport]);

	return (
		<>
			{summary && (
				<Box
					sx={{
						'& > div': {
							flexWrap: 'wrap',
							gap: '1rem',
							marginBottom: '2rem',
							'& > *': { margin: 0 },
						},
					}}
				>
					<AnalyticsCardsContainer>
						<EnrollmentsCard elevation={10}>
							<AnalyticsTotal />
							<TypeInfo>
								<span>{egp(summary.grand_total)}</span>
								<label>Total Sales</label>
							</TypeInfo>
						</EnrollmentsCard>
						{METHOD_KEYS.map((method) => (
							<EnrollmentTypeCard key={method} color={pickMethodColor(method)}>
								<TypeInfo>
									<TypeValue>{egp(summary[method]?.total)}</TypeValue>
								</TypeInfo>
								<TypeName>{`${METHOD_LABELS[method]} (${
									summary[method]?.count ?? 0
								} enrolls)`}</TypeName>
							</EnrollmentTypeCard>
						))}
						<EnrollmentTypeCard color="#ff3100">
							<TypeInfo>
								<TypeValue>{egp(summary.refunded)}</TypeValue>
							</TypeInfo>
							<TypeName>{`Refunded (${summary.refund_count})`}</TypeName>
						</EnrollmentTypeCard>
						<EnrollmentTypeCard color="#00d66b">
							<TypeInfo>
								<TypeValue>{egp(summary.net)}</TypeValue>
							</TypeInfo>
							<TypeName>Net Sales</TypeName>
						</EnrollmentTypeCard>
					</AnalyticsCardsContainer>
				</Box>
			)}

			<Box
				sx={{
					'& thead th': {
						whiteSpace: 'normal',
						padding: '0 0.5rem',
						fontSize: '0.875rem',
					},
				}}
			>
				<MainTable
					tableFilters={[]}
					RowsperPage={meta?.per_page || 10}
					onPageChangeRequest={fetchReport}
					totalPages={meta?.last_page || 1}
					total={meta?.total || 0}
					pageNum={meta?.current_page || 1}
					tableHeads={TABLE_HEADS}
					tableMinWidth="70rem"
					renderItems={() => {
						if (!rows.length) {
							return (
								<TableRow>
									<TableCell colSpan={TABLE_HEADS.length}>
										<NoResults />
									</TableCell>
								</TableRow>
							);
						}
						return rows.map((row, index) => (
							<TableRow key={`${row.list_price}-${row.effective_price}-${row.note}-${index}`}>
								<TableCell align="center">
									{`${money(row.list_price)} - ${money(row.effective_price)}`}
								</TableCell>
								<TableCell align="center">{row.note || '-'}</TableCell>
								{METHOD_KEYS.map((method) => (
									<TableCell key={method} align="center">
										{formula(row, method)}
									</TableCell>
								))}
								<TableCell align="center">{rowEnrollments(row)}</TableCell>
								<TableCell align="center">{egp(rowTotal(row))}</TableCell>
							</TableRow>
						));
					}}
				/>
			</Box>
		</>
	);
};

export default ProductEnrollmentInvoices;
